<template>
    <component
        :is="tag"
        v-bind="linkAttrs"
        class="inline-flex items-center justify-center gap-2 font-semibold whitespace-nowrap transition-colors cursor-pointer select-none aria-disabled:pointer-events-none aria-disabled:opacity-40 disabled:pointer-events-none disabled:opacity-40"
        :class="[variants[variant], sizes[size]]"
    >
        <slot/>
    </component>
</template>

<script lang="ts" setup>
import type { RouteLocationRaw } from 'vue-router'
import { NuxtLink } from '#components'

const props = withDefaults(defineProps<{
    variant?: 'primary' | 'secondary' | 'ghost' | 'danger',
    size?: 'sm' | 'md' | 'lg',
    // Renders a link instead of a button. `to` for app routes, `href` for full page loads / external.
    to?: RouteLocationRaw,
    href?: string,
    nativeType?: 'button' | 'submit',
}>(), {
    variant: 'primary',
    size: 'md',
    nativeType: 'button',
})

const variants = {
    primary: 'bg-accent text-accent-ink hover:bg-accent-hover',
    secondary: 'border border-line-strong text-ink hover:bg-ink hover:text-canvas',
    ghost: 'text-ink underline-offset-4 hover:underline',
    // Destructive actions (delete): a ghost button in the danger color
    danger: 'text-danger underline-offset-4 hover:underline',
}

const sizes = {
    sm: 'h-8 px-3 text-small',
    md: 'h-10 px-4 text-body',
    lg: 'h-12 px-6 text-body',
}

const tag = computed(() => props.to ? NuxtLink : props.href ? 'a' : 'button')
const linkAttrs = computed(() => {
    if (props.to) return { to: props.to }
    if (props.href) return { href: props.href }
    return { type: props.nativeType }
})
</script>
